import { useState } from 'react';
import { ShieldCheck, LogIn, KeyRound, ArrowLeft } from 'lucide-react';
import { useLogin, useMfaRecover, useMfaVerify } from '../../lib/api/queries';

type Phase = 'password' | 'totp' | 'recovery';

/**
 * Two-step sign-in. The user always starts at the password phase; if their
 * account has MFA enabled (every account, post Slice 4c — mandatory MFA),
 * the server responds with {@code requiresTwoFactor: true} and we swap the
 * form for a 6-digit input. From there the user can fall through to a
 * recovery-code input via the "use a recovery code instead" link.
 *
 * The page deliberately doesn't render anything authenticated — App.tsx's
 * gate handles the redirect to the main app once {@code useCurrentUser}
 * fills in. We just collect the credentials.
 */
export function LoginPage() {
  const [phase, setPhase] = useState<Phase>('password');

  // Phase-1 inputs are kept lifted so the user doesn't lose their email if
  // they need to step back from the TOTP phase to fix a typo.
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);

  // Phase-2/3 inputs.
  const [code,         setCode]         = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');

  const login    = useLogin();
  const verify   = useMfaVerify();
  const recover  = useMfaRecover();

  const onPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email, password, rememberMe: remember },
      {
        onSuccess: (result) => {
          if (result.requiresTwoFactor) {
            setPhase('totp');
            setCode('');
          }
          // Else: full sign-in completed — App.tsx gate flips to the main app.
        },
      },
    );
  };

  const onTotpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verify.mutate({ code, rememberMe: remember, rememberMachine: false });
  };

  const onRecoverySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    recover.mutate({ recoveryCode, rememberMe: remember });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          {/* Brand lockup served as a static asset from /public/dpas-logo.png.
              Replace the file at src/DPAS.Client/public/dpas-logo.png to swap
              the artwork — no rebuild needed in dev. */}
          <img
            src="/dpas-logo.png"
            alt="DPAS — Data Privacy Advisory Service"
            className="w-full max-w-xs h-auto"
          />
        </div>

        {phase === 'password' && (
          <PasswordForm
            email={email}        setEmail={setEmail}
            password={password}  setPassword={setPassword}
            remember={remember}  setRemember={setRemember}
            onSubmit={onPasswordSubmit}
            isPending={login.isPending}
            errorMessage={passwordErrorMessage(login.error, login.isError)}
          />
        )}

        {phase === 'totp' && (
          <TotpForm
            code={code} setCode={setCode}
            onSubmit={onTotpSubmit}
            onBack={() => { setPhase('password'); verify.reset(); }}
            onUseRecovery={() => { setPhase('recovery'); verify.reset(); }}
            isPending={verify.isPending}
            errorMessage={mfaErrorMessage(verify.error, verify.isError)}
          />
        )}

        {phase === 'recovery' && (
          <RecoveryForm
            recoveryCode={recoveryCode} setRecoveryCode={setRecoveryCode}
            onSubmit={onRecoverySubmit}
            onBack={() => { setPhase('totp'); recover.reset(); }}
            isPending={recover.isPending}
            errorMessage={mfaErrorMessage(recover.error, recover.isError)}
          />
        )}

        {phase === 'password' && (
          <p className="mt-4 text-xs text-gray-500 text-center">
            Dev users: <span className="font-mono">sammy@acme.test</span> /{' '}
            <span className="font-mono">alex@acme.test</span>
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components — kept inline because each is tightly bound to LoginPage
// state and there's nothing to reuse elsewhere.
// ---------------------------------------------------------------------------

type PasswordFormProps = {
  email: string;        setEmail: (s: string) => void;
  password: string;     setPassword: (s: string) => void;
  remember: boolean;    setRemember: (b: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  errorMessage: string | null;
};

function PasswordForm(p: PasswordFormProps) {
  return (
    <form onSubmit={p.onSubmit}
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h1 className="text-lg font-semibold text-gray-900">Sign in</h1>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Email</span>
        <input
          type="email" required autoFocus autoComplete="email"
          value={p.email} onChange={e => p.setEmail(e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Password</span>
        <input
          type="password" required autoComplete="current-password"
          value={p.password} onChange={e => p.setPassword(e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox"
          checked={p.remember} onChange={e => p.setRemember(e.target.checked)}
          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
        Remember me on this device
      </label>

      {p.errorMessage && (
        <p role="alert" className="text-sm text-danger-500">{p.errorMessage}</p>
      )}

      <button type="submit" disabled={p.isPending}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                   bg-brand-600 text-white rounded-md hover:bg-brand-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        <LogIn className="w-4 h-4" aria-hidden />
        {p.isPending ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

type TotpFormProps = {
  code: string; setCode: (s: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  onUseRecovery: () => void;
  isPending: boolean;
  errorMessage: string | null;
};

function TotpForm(p: TotpFormProps) {
  return (
    <form onSubmit={p.onSubmit}
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h1 className="text-lg font-semibold text-gray-900">Two-factor code</h1>
      <p className="text-sm text-gray-600">
        Open your authenticator app and enter the 6-digit code shown for DpasSuite.
      </p>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Authenticator code</span>
        <input
          // text + inputMode=numeric so authenticator clipboard pastes work
          // and the on-screen keyboard is numeric on mobile.
          type="text" inputMode="numeric" autoComplete="one-time-code" required autoFocus
          pattern="[0-9 ]*" maxLength={9}
          value={p.code} onChange={e => p.setCode(e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md tracking-widest
                     font-mono text-lg
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </label>

      {p.errorMessage && (
        <p role="alert" className="text-sm text-danger-500">{p.errorMessage}</p>
      )}

      <button type="submit" disabled={p.isPending}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                   bg-brand-600 text-white rounded-md hover:bg-brand-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        <ShieldCheck className="w-4 h-4" aria-hidden />
        {p.isPending ? 'Verifying…' : 'Verify and sign in'}
      </button>

      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={p.onBack}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-3 h-3" aria-hidden />
          Back
        </button>
        <button type="button" onClick={p.onUseRecovery}
          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700">
          <KeyRound className="w-3 h-3" aria-hidden />
          Use a recovery code instead
        </button>
      </div>
    </form>
  );
}

type RecoveryFormProps = {
  recoveryCode: string; setRecoveryCode: (s: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  isPending: boolean;
  errorMessage: string | null;
};

function RecoveryForm(p: RecoveryFormProps) {
  return (
    <form onSubmit={p.onSubmit}
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h1 className="text-lg font-semibold text-gray-900">Recovery code</h1>
      <p className="text-sm text-gray-600">
        Enter one of the 10 single-use recovery codes you saved when you set up
        two-factor authentication. After use it's consumed.
      </p>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Recovery code</span>
        <input
          type="text" autoComplete="off" required autoFocus maxLength={20}
          value={p.recoveryCode} onChange={e => p.setRecoveryCode(e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md tracking-wider
                     font-mono text-lg
                     focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </label>

      {p.errorMessage && (
        <p role="alert" className="text-sm text-danger-500">{p.errorMessage}</p>
      )}

      <button type="submit" disabled={p.isPending}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                   bg-brand-600 text-white rounded-md hover:bg-brand-700
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        <KeyRound className="w-4 h-4" aria-hidden />
        {p.isPending ? 'Verifying…' : 'Sign in with recovery code'}
      </button>

      <div className="text-xs">
        <button type="button" onClick={p.onBack}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-3 h-3" aria-hidden />
          Back to authenticator code
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Error message mapping. Server returns 401 for bad creds and 400 for the
// "field missing" / "code doesn't match" cases. We keep messages generic for
// 401 so we don't leak whether the email exists.
// ---------------------------------------------------------------------------

function passwordErrorMessage(err: unknown, isError: boolean): string | null {
  if (!isError) return null;
  const status = (err as { status?: number })?.status;
  if (status === 401) return 'Email or password is incorrect.';
  if (status === 400) return 'Email and password are required.';
  return 'Login failed — please try again.';
}

function mfaErrorMessage(err: unknown, isError: boolean): string | null {
  if (!isError) return null;
  const status = (err as { status?: number })?.status;
  const body   = (err as { body?: unknown })?.body;
  if (status === 401) return 'That code didn\'t match. Try the next code from your app.';
  if (status === 400 && typeof body === 'string') return body;
  if (status === 400) return 'Enter the code from your authenticator app.';
  return 'Verification failed — please try again.';
}
